// cherish3: 배틀 한글화 3차 적용됨
/**
 * Topbar Panel
 *
 * Topbar view - handles the topbar and some generic popups.
 *
 * Also handles global drag-and-drop support.
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import {
	Config, NARROW_MODE_HEADER_WIDTH, PS, type PSRoom, type RoomID, VERTICAL_HEADER_WIDTH,
} from "./client-main";
import { PSView } from "./panels";
import type { Battle } from "./battle";
import { Dex } from "./battle-dex";
import { cwmAskGate, cwmNestIsNew } from "./cherish-market";
import { cwgAskGate, cwgGymOpen } from "./cherish-gym";
import { BattleLog } from "./battle-log"; // optional

window.addEventListener('dragover', e => {
	// this prevents the bounce-back animation
	e.preventDefault();
});

export class PSHeader extends preact.Component {
	faviconNotifying = false;
	updateFavicon = () => {
		const notifying = Object.values(PS.rooms).some(room => room?.notifications.length);
		if (notifying === this.faviconNotifying) return;

		const favicon = document.querySelector<HTMLLinkElement>('#dynamic-favicon');
		if (!favicon) return;
		// cherish-favicon: 스탬프 유지 — 무버전 URL 로 돌아가면 캐시된 상류 아이콘이 되살아난다
		const iconQs = favicon.href.includes('?') ? `?${favicon.href.split('?')[1]}` : '';
		favicon.href = `${Dex.resourcePrefix}${notifying ? 'favicon-notify.ico' : 'favicon.ico'}${iconQs}`;
		this.faviconNotifying = notifying;
	};
	// cherish-mute-dblclick-off (patch-mute-dblclick-off.py): 이 핸들러는 **더 이상 어디에도 연결돼 있지 않다**.
	// 업스트림은 앱바 스피커 아이콘 더블클릭을 전체 음소거 단축키로 썼는데, 모바일에서
	// 「버튼을 눌렀을 뿐인데 소리가 전부 꺼졌다」가 됐다 (2026-09-14 사용자 신고, 실측 재현).
	// 버튼이 28x28 이라 첫 탭이 잘 빗나가고 touch-action: manipulation 으로 탭 지연도 없어서,
	// 두 번 두드리면 팝업이 열렸다 닫히며 소리만 조용히 꺼진다. 음소거는 음량 팝업의
	// 「전체 음소거」 체크박스가 정본이다. ⚠️ 이 핸들러를 버튼에 다시 달지 말 것.
	// 정의를 남겨 둔 건 업스트림 병합 충돌을 줄이기 위해서다.
	static toggleMute = (e: Event) => {
		PS.prefs.set('mute', !PS.prefs.mute);
		PS.update();
	};
	static handleDragEnter = (e: DragEvent) => {
		// console.log('dragenter ' + e.dataTransfer!.dropEffect);
		e.preventDefault();
		if (PS.dragging?.type !== 'room') return;
		/** the element being passed over */
		const target = e.currentTarget as HTMLAnchorElement;

		const draggingRoom = PS.dragging.roomid;
		if (draggingRoom === null) return;

		const draggedOverRoom = PS.router.extractRoomID(target.href);
		if (draggedOverRoom === null) return; // should never happen
		if (draggingRoom === draggedOverRoom) return;

		const leftIndex = PS.leftRoomList.indexOf(draggedOverRoom);
		if (leftIndex >= 0) {
			PS.dragOnto(PS.rooms[draggingRoom]!, 'left', leftIndex);
		} else {
			const rightIndex = PS.rightRoomList.indexOf(draggedOverRoom);
			if (rightIndex >= 0) {
				PS.dragOnto(PS.rooms[draggingRoom]!, 'right', rightIndex);
			} else {
				// eslint-disable-next-line no-useless-return
				return;
			}
		}

		// dropEffect !== 'none' prevents bounce-back animation in
		// Chrome/Safari/Opera
		// if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
	};
	static handleDragStart = (e: DragEvent) => {
		const roomid = PS.router.extractRoomID((e.currentTarget as HTMLAnchorElement).href);
		if (!roomid) return; // should never happen

		PS.dragging = { type: 'room', roomid };
	};
	static roomInfo(room: PSRoom) {
		const RoomType = PS.roomTypes[room.type];
		let icon = RoomType?.icon || <i class="fa fa-file-text-o" aria-hidden></i>;
		let title = room.title;
		switch (room.type) {
		case 'battle':
			const idChunks = room.id.split('-');
			let formatName;
			// TODO: relocate to room implementation
			if (idChunks.length <= 2) {
				if (idChunks[1] === 'uploadedreplay') formatName = 'Uploaded Replay';
			} else {
				formatName = window.BattleLog ? BattleLog.formatName(idChunks[1]) : idChunks[1];
			}
			if (!title) {
				const battle = (room as any).battle as Battle | undefined;
				const p1 = battle?.p1?.name || '';
				const p2 = battle?.p2?.name || '';
				if (p1 && p2) {
					title = `${p1} v. ${p2}`;
				} else if (p1 || p2) {
					title = `${p1}${p2}`;
				} else {
					title = `(empty room)`;
				}
			}
			icon = <i class="text">{formatName}</i>;
			break;
		case 'html':
		default:
			if (title.startsWith('[')) {
				let closeBracketIndex = title.indexOf(']');
				if (closeBracketIndex > 0) {
					icon = <i class="text">{title.slice(1, closeBracketIndex)}</i>;
					title = title.slice(closeBracketIndex + 1);
					break;
				}
			}
			break;
		}
		return { icon, title };
	}
	/** cherish-world fork: 닫을 수 없는 기본 탭 (patch-cherish-tabs.py) */
	static readonly CHERISH_FIXED_TABS = new Set(['cherish-nest', 'cherish-gym', 'cherish-ranking', 'cherish-team']);
	/** cherish-appbar v2: 좁은 화면 하단 알약 내비 (patch-appbar.py). 배틀 화면·넓은 화면에선 null */
	static renderCherishBottomNav() {
		let battleOpen = false;
		for (const id in PS.rooms) {
			const r = PS.rooms[id as RoomID];
			if (r && r.type === 'battle' && PS.isVisiblePanel(r)) { battleOpen = true; break; }
		}
		const narrow = PSView.narrowMode || document.documentElement.clientWidth <= 700;
		document.documentElement.classList.toggle('cw-narrow', narrow); // v6: 배틀 중에도 켜지는 좁은 화면 스위치 (스킨 §30)
		const show = narrow && !battleOpen;
		document.documentElement.classList.toggle('cw-bnav', show);
		if (!show) return null;
		const bnavW = Math.max(240, document.documentElement.clientWidth - 20);
		return <ul class="cw-bnav" role="tablist" style={`width:${bnavW}px`}>
			{(['', 'cherish-team', 'cherish-nest', 'cherish-gym', 'cherish-ranking'] as RoomID[]).map(id => PSHeader.renderRoomTab(id, true))}
		</ul>;
	}
	/** cherish-appbar v7: 고정 탭은 언제나 홈 · 내 포켓몬 · 보금자리 · 랭킹 (patch-appbar.py).
	 * 주소로 방에 바로 들어오면 그 방이 목록에 먼저 들어가 순서가 뒤집힌다 —
	 * 만드는 쪽(panel-mainmenu.tsx)이 아니라 그리는 쪽에서 고정해야 언제 들어오든 같다. */
	static readonly CHERISH_TAB_ORDER = ['', 'cherish-team', 'cherish-nest', 'cherish-ranking'] as RoomID[];
	static cherishLeftRooms(): RoomID[] {
		const rank = (id: RoomID) => {
			const i = PSHeader.CHERISH_TAB_ORDER.indexOf(id);
			return i < 0 ? PSHeader.CHERISH_TAB_ORDER.length : i;
		};
		// 안정 정렬 — 고정 탭 밖(배틀·채팅 방)은 들어온 순서를 그대로 둔다.
		return PS.leftRoomList
			.map((id, i) => [id, i] as [RoomID, number])
			.sort((a, b) => (rank(a[0]) - rank(b[0])) || (a[1] - b[1]))
			.map(pair => pair[0]);
	}
	static renderRoomTab(id: RoomID, noAria?: boolean, includeMiniNotifications = true) {
		// cherish-gym-hide (patch-gym-tab.py): 체육관 탭은 스위치가 켜져 있을 때만 — 게이트가 아직이면 안 그리고, 답이 오면 한 번 다시 그린다.
		if (id === 'cherish-gym') {
			cwgAskGate(() => PS.update());
			if (cwgGymOpen() !== true) return null;
		}
		const room = PS.rooms[id];
		if (!room) return null;
		const closable = (id === '' || id === 'rooms' || PSHeader.CHERISH_FIXED_TABS.has(id) ? '' : ' closable');
		const cur = PS.isVisiblePanel(room) ? ' cur' : '';
		let notifying = room.isSubtleNotifying ? ' subtle-notifying' : '';
		let hoverTitle = '';
		let notifications = room.notifications;
		if (id === '' && includeMiniNotifications) {
			for (const roomid of PS.miniRoomList) {
				const miniNotifications = PS.rooms[roomid]?.notifications;
				if (miniNotifications?.length) notifications = [...notifications, ...miniNotifications];
			}
		}
		if (notifications.length) {
			notifying = ' notifying';
			for (const notif of notifications) {
				if (!notif.body) continue;
				hoverTitle += `${notif.title}\n${notif.body}\n`;
			}
		}
		let className = `roomtab button${notifying}${closable}${cur}`;

		let { icon, title: roomTitle } = PSHeader.roomInfo(room);
		if (room.type === 'rooms' && PS.leftPanelWidth !== null) roomTitle = '';
		if (room.type === 'battle') className += ' roomtab-battle';

		let closeButton = null;
		if (closable) {
			closeButton = <button class="closebutton" name="closeRoom" value={id} aria-label="Close">
				<i class="fa fa-times-circle" aria-hidden></i>
			</button>;
		}
		const aria: Record<string, string> = noAria ? {} : {
			"role": "tab", "id": `roomtab-${id}`, "aria-selected": cur ? "true" : "false",
		};
		if (id === 'rooms') aria['aria-label'] = "접속 유저";
		// cherish-appbar: 홈 + 고정 탭은 알약 내비, 나머지는 닫히는 칩 (patch-appbar.py)
		const navLi = (id === '' || PSHeader.CHERISH_FIXED_TABS.has(id)) ? ' cw-nav-li' : '';
		// cherish-nest-dot (patch-nest-tab.py 7-b): 보금자리에 새로 생긴 것이 있으면 점 하나.
		// 게이트는 이미 있던 요청 하나뿐이고, 답이 오면 그때 한 번만 다시 그린다. 숫자는 안 붙인다.
		let newDot = '';
		if (id === 'cherish-nest') {
			cwmAskGate(() => PS.update());
			if (cwmNestIsNew()) newDot = ' cw-new-dot';
		}
		return <li class={(id === '' ? 'home-li' : '') + navLi + newDot} key={id}>
			<a
				class={className} href={`/${id}`} draggable={true} title={hoverTitle || undefined}
				onDragEnter={this.handleDragEnter} onDragStart={this.handleDragStart}
				{...aria}
			>
				{icon} {roomTitle}
			</a>
			{closeButton}
		</li>;
	}
	static notifyingMiniRoomTabs() {
		return PS.miniRoomList.filter(roomid => PS.rooms[roomid]?.notifications.length);
	}
	handleResize = () => {
		if (!this.base) return;

		if (PS.leftPanelWidth === null) {
			const width = document.documentElement.clientWidth;
			const oldNarrowMode = PSView.narrowMode;
			PSView.narrowMode = width <= 700;
			PSView.verticalHeaderWidth = PSView.narrowMode ? NARROW_MODE_HEADER_WIDTH : VERTICAL_HEADER_WIDTH;
			document.documentElement.style.width = PSView.useCSSScrollSnap() ?
				`${width + NARROW_MODE_HEADER_WIDTH}px` : 'auto';
			if (oldNarrowMode !== PSView.narrowMode) {
				PSView.updateScrollSnap();
				PS.update();
			}
			return;
		}
		if (PSView.narrowMode) {
			document.documentElement.style.width = 'auto';
			PSView.narrowMode = false;
			PSView.updateScrollSnap();
		}

		let userbarLeft = this.base.querySelector('div.userbar .icon.button')?.getBoundingClientRect()?.left;
		if (userbarLeft) userbarLeft -= 5;
		const plusTabRight = this.base.querySelector('a.roomtab[aria-label="접속 유저"]')?.getBoundingClientRect()?.right;
		const overflow = this.base.querySelector<HTMLElement>('.overflow');

		if (!overflow || !userbarLeft || !plusTabRight) return;

		const maintabbar = this.base.querySelector<HTMLElement>('div.maintabbar');
		if (maintabbar) {
			const userbarRight = window.innerWidth - userbarLeft;
			maintabbar.style.marginRight = `${userbarRight}px`;
			overflow.style.right = `${userbarRight}px`;
		}

		if (plusTabRight > userbarLeft - 3) {
			overflow.style.display = 'block';
		} else {
			overflow.style.display = 'none';
		}
	};
	override componentDidMount() {
		PS.user.subscribe(() => {
			this.forceUpdate();
		});
		window.addEventListener('resize', this.handleResize);
		this.handleResize();
		this.updateFavicon();
	}
	override componentDidUpdate() {
		this.handleResize();
		this.updateFavicon();
	}
	// cherish-login-header:begin (patch-login-flow.py)
	/**
	 * 「로그인」 = 디스코드 OAuth 왕복. 본체는 panel-mainmenu 의 cherishLoginStart 다
	 * (포크는 스크립트를 전역으로 컴파일하므로 window 로 건너간다). 아직 안 실렸으면
	 * 같은 주소로 직접 간다 — 이 버튼이 막다른 길이 되는 일만은 없게.
	 */
	cherishHeaderLogin = (ev: Event) => {
		ev.preventDefault();
		const go = (window as any).cherishLoginStart;
		if (go) {
			go(true);
			return;
		}
		location.href = 'https://cherish-world.com/api/auth/discord/login?next=' + encodeURIComponent(location.href);
	};
	// cherish-login-header:end
	renderUser() {
		if (!PS.connection?.connected) {
			return <button class="button" disabled><em>오프라인</em></button>;
		}
		if (PS.user.initializing) {
			return <button class="button" disabled><em>접속 중...</em></button>;
		}
		if (!PS.user.named) {
			// cherish-login-button:begin (patch-login-flow.py)
			// 업스트림은 여기서 이름 입력창(「Choose name」)을 연다. 우리 이름은 길드 서버닉네임
			// 고정이라 그 창은 눌러도 갈 데가 없다 — 디스코드로 바로 보낸다.
			return <button class="button" onClick={this.cherishHeaderLogin}>로그인</button>;
			// cherish-login-button:end
		}
		const userColor = window.BattleLog && `color:${PS.user.away ? '#888' : BattleLog.usernameColor(PS.user.userid)}`;
		return <span class="username" style={userColor}>
			<span class="usernametext">{PS.user.name}</span>
		</span>;
	}
	renderVertical() {
		const miniRoomTabs = PSHeader.notifyingMiniRoomTabs();
		return <div
			id="header" class="header-vertical" role="navigation"
			style={`width:${PSView.verticalHeaderWidth - 7}px`} onClick={PSView.scrollToHeader}
		>
			<div class="maintabbarbottom"></div>
			<div class="scrollable-part">
				{/* cherish-appbar v3: 브랜드 마크 = 파비콘 그림 (patch-appbar.py). 외형은 cherish-skin.css §12·§19 */}
				<a class="cw-brand" href="/" title="구름 배틀 홈">
					<span class="cw-brand-mark has-img"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden><circle cx="12" cy="12" r="9.5" /><path d="M2.5 12h7M14.5 12h7" /><circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none" /></svg></span>
					<span class="cw-brand-name">구름 배틀<small>CHERISH WORLD</small></span>
				</a>
				<div class="tablist" role="tablist">
					<ul>
						{PSHeader.renderRoomTab(PSHeader.cherishLeftRooms()[0], false, false)}
						{miniRoomTabs.map(roomid => PSHeader.renderRoomTab(roomid))}
					</ul>
					<ul>
						{PSHeader.cherishLeftRooms().slice(1).map(roomid => PSHeader.renderRoomTab(roomid))}
					</ul>
					<ul class="siderooms">
						{PS.rightRoomList.map(roomid => PSHeader.renderRoomTab(roomid))}
					</ul>
				</div>
			</div>
			<div class="userbar">
				{this.renderUser()} {}
				<div style="float:right">
					<button class="icon button" data-href="volume" title="소리" aria-label="소리">
						<i class={PS.prefs.mute ? 'fa fa-volume-off' : 'fa fa-volume-up'}></i>
					</button> {}
					<button class="icon button" data-href="options" title="Options" aria-label="Options">
						<i class="fa fa-cog" aria-hidden></i>
					</button>
				</div>
			</div>
			{null /* maintabbar */}
			{null /* overflow */}
			{PSHeader.renderCherishBottomNav()}
		</div>;
	}
	override render() {
		if (PS.leftPanelWidth === null) {
			return this.renderVertical();
		}
		return <div id="header" class="header" role="navigation">
			<div class="maintabbarbottom"></div>
						{/* cherish-appbar: 브랜드 마크 (patch-appbar.py). 외형은 cherish-skin.css §4 */}
			<a class="cw-brand" href="/" title="구름 배틀 홈">
				<span class="cw-brand-mark has-img"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden><circle cx="12" cy="12" r="9.5" /><path d="M2.5 12h7M14.5 12h7" /><circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none" /></svg></span>
				<span class="cw-brand-name">구름 배틀<small>CHERISH WORLD</small></span>
			</a>
			<div class="userbar">
				{this.renderUser()} {}
				<button class="icon button" data-href="volume" title="소리" aria-label="소리">
					<i class={PS.prefs.mute ? 'fa fa-volume-off' : 'fa fa-volume-up'}></i>
				</button> {}
				<button class="icon button" data-href="options" title="Options" aria-label="Options">
					<i class="fa fa-cog" aria-hidden></i>
				</button>
			</div>
			<div class="tabbar maintabbar"><div class="inner-1" role={PS.leftPanelWidth ? 'none' : 'tablist'}><div class="inner-2">
				<ul class="maintabbar-left" style={{ width: `${PS.leftPanelWidth}px` }} role={PS.leftPanelWidth ? 'tablist' : 'none'}>
					{PSHeader.renderRoomTab(PSHeader.cherishLeftRooms()[0])}
					{PSHeader.cherishLeftRooms().slice(1).map(roomid => PSHeader.renderRoomTab(roomid))}
				</ul>
				<ul class="maintabbar-right" role={PS.leftPanelWidth ? 'tablist' : 'none'}>
					{PS.rightRoomList.map(roomid => PSHeader.renderRoomTab(roomid))}
				</ul>
			</div></div></div>
			{PSHeader.renderCherishBottomNav()}
		</div>;
	}
}

export class PSMiniHeader extends preact.Component {
	menuOpen?: boolean;
	override componentDidMount() {
		PSView.addScrollListener(this.handleScroll);
	}
	override componentWillUnmount() {
		PSView.removeScrollListener(this.handleScroll);
	}
	handleScroll = () => {
		if (this.menuOpen !== !PSView.getScrollX()) this.forceUpdate();
	};
	override render() {
		this.menuOpen = !PSView.getScrollX();

		if (PS.leftPanelWidth !== null) return null;

		const notificationsCount = Object.values(PS.rooms).filter(
			room => room !== PS.room && room?.notifications.length
		).length;
		const { icon, title } = PSHeader.roomInfo(PS.panel);
		const userColor = window.BattleLog && `color:${PS.user.away ? '#888' : BattleLog.usernameColor(PS.user.userid)}`;
		const showMenuButton = PSView.narrowMode;
		const notifying = (
			!showMenuButton && this.menuOpen && Object.values(PS.rooms).some(room => room!.notifications.length)
		) ? ' notifying' : '';
		const menuButton = !showMenuButton ? (
			null
		) : !this.menuOpen ? (
			<button onClick={PSView.scrollToHeader} class={`mini-header-left ${notifying}`} aria-label="Menu">
				{!!notificationsCount && <div class="notification-badge">{notificationsCount}</div>}
				<i class="fa fa-bars" aria-hidden></i>
			</button>
		) : (
			<button onClick={PSView.scrollToRoom} class="mini-header-left" aria-label="Menu">
				<i class="fa fa-arrow-right" aria-hidden></i>
			</button>
		);
		return <div class="mini-header" style={`left:${PSView.verticalHeaderWidth + (PSView.narrowMode ? 0 : -1)}px;`}>
			{menuButton}
			{icon} {title}
			<button data-href="options" class="mini-header-right" aria-label="Options">
				{PS.user.named ? <strong style={userColor}>{PS.user.name}</strong> : <i class="fa fa-cog" aria-hidden></i>}
			</button>
		</div>;
	}
}

preact.render(<PSView />, document.body, document.getElementById('ps-frame')!);
